import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { useAuth } from '@restworld/feature-authentication';

import { LayoutWrapper } from '../../../worker-app/layouts';
import { useCvParsingJobStatus, useFetchWorkerDataQuery } from '../../hooks';
import { Header } from './header';
import { setWorkerFieldOptionsAtom } from '../global-state.worker-data';
import ToastMessage from '../toast-message';
import { useWorkerFieldOptions } from '../worker-application-wizard';
import WorkerManagedUsingKlaaryo, {
  useOpenApplicationConfirmation,
  wizardCreatedApplicationAtom,
  workerManagedFooterMapKeyAtom,
} from '../worker-managed-using-klaaryo';
import { InAppInterviewConfirmationModal } from '../worker-entry-point/worker-entry-point.components/in-app-assessment-confirmation';
import { ProfileDetailsSection } from '../worker-references';
import { UserProfileDetails } from './user-profile-details';
import { cn } from '@restworld/utils-common';

export function ProfileComponent() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const openApplicationConfirmation = useOpenApplicationConfirmation();
  const wizardCreatedApplication = useAtomValue(wizardCreatedApplicationAtom);
  const setWizardCreatedApplication = useSetAtom(wizardCreatedApplicationAtom);
  const setWorkerManagedFooterMapKeyAtom = useSetAtom(workerManagedFooterMapKeyAtom);

  const router = useRouter();

  useFetchWorkerDataQuery();
  useCvParsingJobStatus();

  const options = useWorkerFieldOptions({ isFetchStatusOptions: false });
  const onSetWorkerFieldOptionsData = useSetAtom(setWorkerFieldOptionsAtom);

  useEffect(() => {
    if (options) onSetWorkerFieldOptionsData(options);
  }, [options, onSetWorkerFieldOptionsData]);

  useEffect(() => {
    if (router.query?.manage_using_klaaryo === 'true') {
      // The end of the manual wizard. The confirmation follows the interview channel of the
      // application the wizard created: in-app opens Modal B (into the chat), WhatsApp the
      // WhatsApp step, unknown (a reload lost it) the in-app step without a button. Consumed
      // once, so a later visit to the profile does not reuse it.
      openApplicationConfirmation(wizardCreatedApplication?.application, {
        // The wizard's own position, never the one left in the page atom by an earlier visit.
        jobPosition: wizardCreatedApplication?.jobPosition ?? {},
      });
      setWizardCreatedApplication(undefined);
      setWorkerManagedFooterMapKeyAtom('profile');
    }
    // Only the query decides when this runs: the application is read once, at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query?.manage_using_klaaryo]);

  const showReferenceFeature = process.env.NEXT_PUBLIC_SHOW_REFERENCES_FEATURE === 'true';

  return (
    <>
      <LayoutWrapper
        title={t('onboarding-page:profile')}
        hasFooter
        hasNavbar
        showSearchBarInNavbar={user?.type !== 'employer'}
        testId="navbar-for-profile-page"
        className="bg-neutral-10"
      >
        <ProfileWrapper>
          <ToastMessage />
          <div className="box-border flex lg:flex-row flex-col-reverse  lg:justify-center justify-start  lg:gap-10 gap-8 w-full max-w-full">
            <div className="flex flex-col items-start flex-1">
              <Header />
            </div>

            <div className="flex flex-row lg:justify-start md:justify-center justify-start">
              {showReferenceFeature ? <ProfileDetailsSection /> : <UserProfileDetails />}
            </div>
          </div>
        </ProfileWrapper>
      </LayoutWrapper>
      <WorkerManagedUsingKlaaryo />
      <InAppInterviewConfirmationModal />
    </>
  );
}

const ProfileWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('relative flex box-border flex-col items-center max-w-full', className)} {...props} />
);
